import { X } from "lucide-react";
import * as React from "react";

import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Box } from "../../components/ui/box";
import type { SpacingToken } from "../../components/ui/box-classes";
import { Button, type ButtonProps } from "../../components/ui/button";
import { Icon } from "../../components/ui/icon";
import {
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
} from "../../components/ui/menu";
import {
  Popover,
  PopoverPopup,
  PopoverTrigger,
} from "../../components/ui/popover";
import { Text } from "../../components/ui/text";
import { toastManager } from "../../components/ui/toast";
import { useIsMobile } from "../../hooks/use-media-query";
import { cn } from "../../lib/utils";

import type {
  BulkAction,
  BulkActionConfirm,
  BulkActionDirect,
  BulkActionOptionPicker,
  BulkActionPopover,
  SelectAllGroup,
} from "./bulk-actions-bar-types";
import type { Omittable } from "../../lib/props";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export type BulkActionsBarProps<TData> = {
  actions: BulkAction<TData>[];
  /** The selected row ids. The bar renders nothing while this is empty. */
  selectedIds: ReadonlySet<string>;
  onClearSelection: () => void;
  /**
   * Row data for every selected id, accumulated by the consumer across page
   * loads so a selection survives page eviction during virtual scroll. It is
   * the ONLY selection source: the consumer fills it in the same render that
   * produces `selectedIds`, from the same rows, so nothing selectable can be
   * selected and missing here.
   */
  selectedRowData: Map<string, TData>;
  /** Groups where "select all" is active (includes unloaded rows). */
  selectAllGroups: SelectAllGroup[];
};

// ---------------------------------------------------------------------------
// Shared icon renderer
// ---------------------------------------------------------------------------

function ActionIcon({
  icon,
  size = "sm",
  color,
  className,
}: Omittable<{
  icon: BulkAction<unknown>["icon"];
  size?: "sm" | "xs";
  color?: "muted";
  className?: string;
}>): React.ReactElement | null {
  if (!icon) return null;
  return typeof icon === "string" ? (
    <Icon className={className} color={color} name={icon} size={size} />
  ) : (
    <Icon as={icon} className={className} color={color} size={size} />
  );
}

// ---------------------------------------------------------------------------
// Action execution
// ---------------------------------------------------------------------------

type ExecuteBulkActionOptions = Omittable<{
  actionId: string;
  actionFn: () => Promise<void> | void;
  label: string;
  count: number;
  onClearSelection: () => void;
  setLoadingActionId: (id: string | null) => void;
  // An action with nothing to announce leaves it out, and a caller threading
  // through an optional message means the same thing.
  successMessage?: string | undefined;
}>;

async function executeBulkAction({
  actionId,
  actionFn,
  label,
  count,
  onClearSelection,
  setLoadingActionId,
  successMessage,
}: ExecuteBulkActionOptions): Promise<void> {
  setLoadingActionId(actionId);
  let succeeded = false;
  try {
    await actionFn();
    succeeded = true;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : "Unknown error";
    toastManager.add({ title: `${label} failed: ${message}`, type: "error" });
  } finally {
    setLoadingActionId(null);
  }
  if (succeeded) {
    toastManager.add({
      title:
        successMessage ??
        `${label} applied to ${count} item${count !== 1 ? "s" : ""}`,
      type: "success",
    });
    onClearSelection();
  }
}

// ---------------------------------------------------------------------------
// Bar action chip
// The bar is an elevated overlay, so plain outline Buttons read correctly on
// it. Destructive actions keep the ghost + destructive-tint chip.
// ---------------------------------------------------------------------------

type BarButtonProps = ButtonProps & Omittable<{ destructive?: boolean }>;

function BarButton({
  destructive,
  className,
  ...props
}: BarButtonProps): React.ReactElement {
  return (
    <Button
      className={cn(
        destructive &&
          "border-destructive/50 bg-destructive/10 text-destructive-foreground hover:bg-destructive/15",
        className,
      )}
      size="sm"
      variant={destructive ? "ghost" : "outline"}
      {...props}
    />
  );
}

// ---------------------------------------------------------------------------
// Popover content frame
// PopoverPopup owns a fixed viewport padding, so the `padding`/`width` props
// are honored by cancelling that padding and re-applying the requested
// token/width on an inner Box.
// ---------------------------------------------------------------------------

function PopoverContentFrame({
  padding,
  width,
  children,
}: Omittable<{
  padding?: SpacingToken;
  width?: number;
  children: React.ReactNode;
}>): React.ReactElement {
  if (padding == null && width == null) return <>{children}</>;
  return (
    <Box
      className={cn(
        padding != null && "-mx-(--viewport-inline-padding) -my-4",
      )}
      padding={padding}
      style={width != null ? { width } : undefined}
    >
      {children}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Per-action button components
// ---------------------------------------------------------------------------

type ActionButtonSharedProps<TData> = {
  selectedRows: TData[];
  displayCount: number;
  selectAllGroups: SelectAllGroup[];
  loadingActionId: string | null;
  onClearSelection: () => void;
  setLoadingActionId: (id: string | null) => void;
};

function DirectActionButton<TData>({
  action,
  selectedRows,
  displayCount,
  loadingActionId,
  onClearSelection,
  setLoadingActionId,
}: ActionButtonSharedProps<TData> & {
  action: BulkActionDirect<TData>;
}): React.ReactElement {
  const isLoading = loadingActionId === action.id;
  const isDisabled = loadingActionId !== null && !isLoading;

  return (
    <BarButton
      destructive={action.destructive}
      disabled={isDisabled}
      loading={isLoading}
      onClick={() =>
        executeBulkAction({
          actionId: action.id,
          actionFn: () => action.onAction(selectedRows),
          label: action.label,
          // The header and popover actions announce displayCount (the
          // select-all total, including unloaded rows) — the toast must match.
          count: displayCount,
          onClearSelection,
          setLoadingActionId,
        })
      }
    >
      <ActionIcon icon={action.icon} />
      {action.label}
    </BarButton>
  );
}

function OptionPickerActionButton<TData>({
  action,
  selectedRows,
  displayCount,
  loadingActionId,
  onClearSelection,
  setLoadingActionId,
}: ActionButtonSharedProps<TData> & {
  action: BulkActionOptionPicker<TData>;
}): React.ReactElement {
  const isLoading = loadingActionId === action.id;
  const isDisabled = loadingActionId !== null && !isLoading;

  return (
    <Menu>
      <MenuTrigger
        render={
          <BarButton
            destructive={action.destructive}
            disabled={isDisabled}
            loading={isLoading}
          />
        }
      >
        <ActionIcon icon={action.icon} />
        {action.label}
      </MenuTrigger>
      <MenuPopup align="end" side="top">
        {action.options.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() =>
              executeBulkAction({
                actionId: action.id,
                actionFn: () => action.onAction(selectedRows, option.id),
                label: action.label,
                count: displayCount,
                onClearSelection,
                setLoadingActionId,
              })
            }
          >
            <ActionIcon color="muted" icon={option.icon} />
            {option.label}
          </MenuItem>
        ))}
      </MenuPopup>
    </Menu>
  );
}

function ConfirmActionButton<TData>({
  action,
  selectedRows,
  displayCount,
  loadingActionId,
  onClearSelection,
  setLoadingActionId,
}: ActionButtonSharedProps<TData> & {
  action: BulkActionConfirm<TData>;
}): React.ReactElement {
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const isLoading = loadingActionId === action.id;
  const isDisabled = loadingActionId !== null && !isLoading;

  const title = resolveConfirmText(action.confirmTitle, selectedRows);
  const description = resolveConfirmText(
    action.confirmDescription,
    selectedRows,
  );

  return (
    <AlertDialog onOpenChange={setConfirmOpen} open={confirmOpen}>
      <BarButton
        destructive={action.destructive}
        disabled={isDisabled}
        loading={isLoading}
        onClick={() => setConfirmOpen(true)}
      >
        <ActionIcon icon={action.icon} />
        {action.label}
      </BarButton>
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogClose render={<Button variant="outline" />}>
            Cancel
          </AlertDialogClose>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              executeBulkAction({
                actionId: action.id,
                actionFn: () => action.onAction(selectedRows),
                label: action.label,
                count: displayCount,
                onClearSelection,
                setLoadingActionId,
              });
            }}
            variant={action.destructive ? "destructive" : "default"}
          >
            {action.confirmLabel ?? action.label}
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}

/**
 * Inner content wrapper for popover actions. Base UI exposes no context hook
 * for closing a popover from inside it, so the root is controlled and its
 * open-change handler is passed down as `onOpenChange` for programmatic close
 * on commit.
 */
function PopoverActionContent<TData>({
  action,
  selectedRows,
  displayCount,
  selectAllGroups,
  onClearSelection,
  setLoadingActionId,
  closeActionRef,
  closeMessageRef,
  onOpenChange,
}: {
  action: BulkActionPopover<TData>;
  selectedRows: TData[];
  displayCount: number;
  selectAllGroups: SelectAllGroup[];
  onClearSelection: () => void;
  setLoadingActionId: (id: string | null) => void;
  closeActionRef: React.MutableRefObject<
    ((rows: TData[]) => Promise<void> | void) | null
  >;
  closeMessageRef: React.MutableRefObject<string | undefined>;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const commit = React.useCallback(
    (
      actionFn: (selectedRows: TData[]) => Promise<void> | void,
      successMessage?: string,
    ) => {
      closeActionRef.current = null;
      closeMessageRef.current = undefined;
      onOpenChange(false);
      executeBulkAction({
        actionId: action.id,
        actionFn: () => actionFn(selectedRows),
        label: action.label,
        count: displayCount,
        onClearSelection,
        setLoadingActionId,
        successMessage,
      });
    },
    [
      action.id,
      action.label,
      selectedRows,
      displayCount,
      onClearSelection,
      setLoadingActionId,
      onOpenChange,
      closeActionRef,
      closeMessageRef,
    ],
  );

  const setCloseAction = React.useCallback(
    (
      actionFn: ((selectedRows: TData[]) => Promise<void> | void) | null,
      successMessage?: string,
    ) => {
      closeActionRef.current = actionFn;
      closeMessageRef.current = successMessage;
    },
    [closeActionRef, closeMessageRef],
  );

  return (
    <>{action.content({ selectedRows, selectAllGroups, commit, setCloseAction })}</>
  );
}

function PopoverActionButton<TData>({
  action,
  selectedRows,
  displayCount,
  selectAllGroups,
  loadingActionId,
  onClearSelection,
  setLoadingActionId,
}: ActionButtonSharedProps<TData> & {
  action: BulkActionPopover<TData>;
}): React.ReactElement {
  const isLoading = loadingActionId === action.id;
  const isDisabled = loadingActionId !== null && !isLoading;
  const [open, setOpen] = React.useState(false);
  const closeActionRef = React.useRef<
    ((rows: TData[]) => Promise<void> | void) | null
  >(null);
  const closeMessageRef = React.useRef<string | undefined>(undefined);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next);
      if (!next && closeActionRef.current) {
        const actionFn = closeActionRef.current;
        const successMessage = closeMessageRef.current;
        closeActionRef.current = null;
        closeMessageRef.current = undefined;
        executeBulkAction({
          actionId: action.id,
          actionFn: () => actionFn(selectedRows),
          label: action.label,
          count: displayCount,
          onClearSelection,
          setLoadingActionId,
          successMessage,
        });
      }
    },
    [
      action.id,
      action.label,
      selectedRows,
      displayCount,
      onClearSelection,
      setLoadingActionId,
    ],
  );

  return (
    <Popover onOpenChange={handleOpenChange} open={open}>
      <PopoverTrigger
        render={
          <BarButton
            destructive={action.destructive}
            disabled={isDisabled}
            loading={isLoading}
          />
        }
      >
        <ActionIcon icon={action.icon} />
        {action.label}
      </PopoverTrigger>
      <PopoverPopup
        align={action.contentProps?.align ?? "end"}
        className={action.contentProps?.className}
        side={action.contentProps?.side ?? "top"}
      >
        <PopoverContentFrame
          padding={action.contentProps?.padding}
          width={action.contentProps?.width}
        >
          <PopoverActionContent
            action={action}
            closeActionRef={closeActionRef}
            closeMessageRef={closeMessageRef}
            displayCount={displayCount}
            onClearSelection={onClearSelection}
            onOpenChange={handleOpenChange}
            selectAllGroups={selectAllGroups}
            selectedRows={selectedRows}
            setLoadingActionId={setLoadingActionId}
          />
        </PopoverContentFrame>
      </PopoverPopup>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Mobile bulk actions sheet.
// Renders each action's content inline within a single popover — nesting
// overlays per action on mobile renders as a blank screen.
// ---------------------------------------------------------------------------

function resolveConfirmText<TData>(
  value: string | ((rows: TData[]) => string),
  rows: TData[],
): string {
  return typeof value === "function" ? value(rows) : value;
}

type MobileSharedProps<TData> = {
  visibleActions: BulkAction<TData>[];
  selectedRows: TData[];
  displayCount: number;
  selectAllGroups: SelectAllGroup[];
  onClearSelection: () => void;
  loadingActionId: string | null;
  setLoadingActionId: (id: string | null) => void;
};

function MobileBulkActionsSheet<TData>({
  visibleActions,
  selectedRows,
  displayCount,
  selectAllGroups,
  onClearSelection,
  loadingActionId,
  setLoadingActionId,
}: MobileSharedProps<TData>): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [activeActionId, setActiveActionId] = React.useState<string | null>(
    null,
  );
  const closeActionRef = React.useRef<
    ((rows: TData[]) => Promise<void> | void) | null
  >(null);
  const closeMessageRef = React.useRef<string | undefined>(undefined);
  const activeAction =
    activeActionId != null
      ? (visibleActions.find((a) => a.id === activeActionId) ?? null)
      : null;

  const runDeferred = React.useCallback(() => {
    const actionFn = closeActionRef.current;
    if (!actionFn || !activeAction) return;
    const message = closeMessageRef.current;
    closeActionRef.current = null;
    closeMessageRef.current = undefined;
    executeBulkAction({
      actionId: activeAction.id,
      actionFn: () => actionFn(selectedRows),
      label: activeAction.label,
      count: displayCount,
      onClearSelection,
      setLoadingActionId,
      successMessage: message,
    });
  }, [
    activeAction,
    selectedRows,
    displayCount,
    onClearSelection,
    setLoadingActionId,
  ]);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next);
      if (!next) {
        runDeferred();
        setActiveActionId(null);
      }
    },
    [runDeferred],
  );

  const commit = React.useCallback(
    (
      actionFn: (rows: TData[]) => Promise<void> | void,
      successMessage?: string,
    ) => {
      if (!activeAction) return;
      closeActionRef.current = null;
      closeMessageRef.current = undefined;
      setActiveActionId(null);
      setOpen(false);
      executeBulkAction({
        actionId: activeAction.id,
        actionFn: () => actionFn(selectedRows),
        label: activeAction.label,
        count: displayCount,
        onClearSelection,
        setLoadingActionId,
        successMessage,
      });
    },
    [
      activeAction,
      selectedRows,
      displayCount,
      onClearSelection,
      setLoadingActionId,
    ],
  );

  const setCloseAction = React.useCallback(
    (
      actionFn: ((rows: TData[]) => Promise<void> | void) | null,
      successMessage?: string,
    ) => {
      closeActionRef.current = actionFn;
      closeMessageRef.current = successMessage;
    },
    [],
  );

  const handleActionClick = React.useCallback(
    (action: BulkAction<TData>) => {
      if (action.type === "direct") {
        setOpen(false);
        executeBulkAction({
          actionId: action.id,
          actionFn: () => action.onAction(selectedRows),
          label: action.label,
          count: displayCount,
          onClearSelection,
          setLoadingActionId,
        });
        return;
      }
      setActiveActionId(action.id);
    },
    [selectedRows, displayCount, onClearSelection, setLoadingActionId],
  );

  const handleBack = React.useCallback(() => {
    runDeferred();
    setActiveActionId(null);
  }, [runDeferred]);

  return (
    <Popover onOpenChange={handleOpenChange} open={open}>
      <PopoverTrigger
        render={
          <BarButton
            disabled={loadingActionId !== null}
            loading={loadingActionId !== null}
          />
        }
      >
        Actions
      </PopoverTrigger>
      <PopoverPopup align="end">
        {/* The action LIST takes a fixed width; a detail body sizes itself —
            an inline selector carries its own min-width (320px), and pinning
            the frame narrower clips it against the popup edge. */}
        <PopoverContentFrame
          padding="none"
          width={
            activeAction && activeAction.type !== "direct" ? undefined : 288
          }
        >
          {activeAction && activeAction.type !== "direct" ? (
            <MobileActionDetail
              action={activeAction}
              commit={commit}
              onBack={handleBack}
              selectAllGroups={selectAllGroups}
              selectedRows={selectedRows}
              setCloseAction={setCloseAction}
            />
          ) : (
            <Box gap="sm" padding="sm">
              {visibleActions.map((action) => {
                const isLoading = loadingActionId === action.id;
                const isDisabled = loadingActionId !== null && !isLoading;
                return (
                  <BarButton
                    destructive={action.destructive}
                    disabled={isDisabled}
                    key={action.id}
                    loading={isLoading}
                    onClick={() => handleActionClick(action)}
                  >
                    <ActionIcon icon={action.icon} />
                    {action.label}
                  </BarButton>
                );
              })}
            </Box>
          )}
        </PopoverContentFrame>
      </PopoverPopup>
    </Popover>
  );
}

type MobileActionDetailAction<TData> = Exclude<
  BulkAction<TData>,
  BulkActionDirect<TData>
>;

function MobileActionDetail<TData>({
  action,
  selectedRows,
  selectAllGroups,
  commit,
  setCloseAction,
  onBack,
}: Omittable<{
  action: MobileActionDetailAction<TData>;
  selectedRows: TData[];
  selectAllGroups: SelectAllGroup[];
  commit: (
    actionFn: (rows: TData[]) => Promise<void> | void,
    successMessage?: string,
  ) => void;
  setCloseAction: (
    actionFn: ((rows: TData[]) => Promise<void> | void) | null,
    successMessage?: string,
  ) => void;
  onBack: () => void;
}>): React.ReactElement {
  return (
    <Box>
      <Box alignSelf="start"
        align="center"
        as="button"
        direction="row"
        gap="xs"
        onClick={onBack}
        paddingX="md"
        paddingY="sm"
      >
        <Icon color="muted" name="chevron-left" size="lg" />
        <Text size="sm" variant="muted" weight="normal">
          Back
        </Text>
      </Box>
      {renderMobileActionBody(
        action,
        selectedRows,
        selectAllGroups,
        commit,
        setCloseAction,
        onBack,
      )}
    </Box>
  );
}

function renderMobileActionBody<TData>(
  action: MobileActionDetailAction<TData>,
  selectedRows: TData[],
  selectAllGroups: SelectAllGroup[],
  commit: (
    actionFn: (rows: TData[]) => Promise<void> | void,
    successMessage?: string,
  ) => void,
  setCloseAction: (
    actionFn: ((rows: TData[]) => Promise<void> | void) | null,
    successMessage?: string,
  ) => void,
  onCancel: () => void,
): React.ReactNode {
  if (action.type === "option-picker") {
    return (
      <Box gap="xs" padding="sm">
        {action.options.map((option) => (
          <Button
            key={option.id}
            onClick={() => commit((rows) => action.onAction(rows, option.id))}
            size="sm"
            variant="ghost"
          >
            <ActionIcon color="muted" icon={option.icon} />
            {option.label}
          </Button>
        ))}
      </Box>
    );
  }

  if (action.type === "confirm") {
    const title = resolveConfirmText(action.confirmTitle, selectedRows);
    const description = resolveConfirmText(
      action.confirmDescription,
      selectedRows,
    );
    return (
      <Box gap="md" padding="sm">
        <Text weight="semibold">{title}</Text>
        <Text size="sm" variant="muted">
          {description}
        </Text>
        <Box direction="row" gap="sm" justify="end">
          <Button onClick={onCancel} size="sm" variant="ghost">
            Cancel
          </Button>
          <Button
            onClick={() => commit((rows) => action.onAction(rows))}
            size="sm"
            variant={action.destructive ? "destructive" : "default"}
          >
            {action.confirmLabel ?? action.label}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box padding={action.contentProps?.padding === "none" ? undefined : "sm"}>
      {action.content({ selectedRows, selectAllGroups, commit, setCloseAction })}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main bar component
// ---------------------------------------------------------------------------

export function BulkActionsBar<TData>({
  actions,
  selectedIds,
  onClearSelection,
  selectedRowData,
  selectAllGroups,
}: BulkActionsBarProps<TData>): React.ReactElement | null {
  const isMobile = useIsMobile();
  const [loadingActionId, setLoadingActionId] = React.useState<string | null>(
    null,
  );

  // Derived on every render rather than memoized: `selectedRowData` is a
  // stable ref the consumer mutates in place as pages load, so no dependency
  // list could see it change. The work is O(selected rows), and every child
  // below re-renders with this component anyway.
  const selectedRows = Array.from(selectedIds, (id) => selectedRowData.get(id))
    .filter((r): r is TData => r != null);

  // When select-all is active, display the total count (including unloaded rows)
  const displayCount =
    selectAllGroups.length > 0
      ? selectAllGroups.reduce((sum, g) => sum + g.totalCount, 0)
      : selectedRows.length;

  const visibleActions = actions.filter((a) => !a.hidden?.(selectedRows));
  if (
    (selectedRows.length === 0 && selectAllGroups.length === 0) ||
    visibleActions.length === 0
  ) {
    return null;
  }

  const shared: ActionButtonSharedProps<TData> = {
    selectedRows,
    displayCount,
    selectAllGroups,
    loadingActionId,
    onClearSelection,
    setLoadingActionId,
  };

  const renderActionButton = (
    action: BulkAction<TData>,
  ): React.ReactElement => {
    switch (action.type) {
      case "direct":
        return <DirectActionButton action={action} key={action.id} {...shared} />;
      case "option-picker":
        return (
          <OptionPickerActionButton action={action} key={action.id} {...shared} />
        );
      case "confirm":
        return <ConfirmActionButton action={action} key={action.id} {...shared} />;
      case "popover":
        return <PopoverActionButton action={action} key={action.id} {...shared} />;
      default: {
        const _exhaustive: never = action;
        return _exhaustive;
      }
    }
  };

  return (
    <Box position="absolute" zIndex={40} paddingX="sm"
      align="center"
      className="bottom-4 left-1/2 -translate-x-1/2 animate-slide-up-fade border py-1.5"
      direction="row"
      gap="sm"
      // The bar floats over the whole view, so it is its own surface — popover
      // — regardless of what the table underneath it paints.
      surface="popover"
      rounded="lg"
    >
      {/* Count chip doubles as the deselect-all control: a labelled, wash-over
          chip with a trailing × reads as "removable", so clearing the whole
          selection is one obvious click rather than a bare icon that scans as
          "close". */}
      <Box
        as="button"
        type="button"
        aria-label="Deselect all"
        onClick={onClearSelection}
        align="center"
        direction="row"
        gap="2xs"
        shrink={false}
        className="bg-wash-active"
        paddingX="sm"
        rounded="md"
        hoverBackground
      >
        {/* The bar is width-constrained on narrow viewports; without nowrap
            the flexed chip wraps "N selected" onto two lines. */}
        <Text size="sm" weight="medium" className="whitespace-nowrap">
          {displayCount} selected
        </Text>
        <Icon as={X} size="sm" />
      </Box>

      {isMobile ? (
        <MobileBulkActionsSheet
          displayCount={displayCount}
          loadingActionId={loadingActionId}
          onClearSelection={onClearSelection}
          selectAllGroups={selectAllGroups}
          selectedRows={selectedRows}
          setLoadingActionId={setLoadingActionId}
          visibleActions={visibleActions}
        />
      ) : (
        <Box align="center" direction="row" gap="sm">
          {visibleActions.map(renderActionButton)}
        </Box>
      )}
    </Box>
  );
}
